import type { Community } from '@communities/community/domain/Community';
import { formatErrorMessages } from '@core/adapters/errors/transformError';
import { MessageToast, Pagination } from '@core/components';
import { AuditSessionsFilters } from '@internal-audit/components/AuditSessionsFilters/AuditSessionsFilters';
import type { AuditSessionFilters, AuditSessionSort, FilterOptions, AuditSessionPageSize } from '@internal-audit/components/AuditSessionsFilters/types';
import { AuditSessionsPageHeader } from '@internal-audit/components/AuditSessionsPageHeader';
import { AuditSessionsTable } from '@internal-audit/components/AuditSessionsTable';
import { CreateAuditSessionDialog } from '@internal-audit/components/CreateAuditSessionDialog';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { useCustomValuesDiscovery } from '@internal-audit/hooks';
import { useEffect, useState } from 'react';

import * as styles from '../AuditSessionsListPage.css';

interface SessionsPageContentProps {
  community: Community | null;
  filteredAndSortedSessions: FlexibleAuditSession[];
  filters: AuditSessionFilters;
  sort: AuditSessionSort;
  filterOptions: FilterOptions;
  sessionsLoading: boolean;
  filterOptionsLoading: boolean;
  isCreateDialogOpen: boolean;
  communityId: string;
  // Pagination props
  currentPage: number;
  totalPages: number;
  pageSize: AuditSessionPageSize;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AuditSessionPageSize) => void;
  // Event handlers
  onFiltersChange: (filters: AuditSessionFilters) => void;
  onSortChange: (sort: AuditSessionSort) => void;
  onViewSession: (session: FlexibleAuditSession) => void;
  onDeleteSuccess: () => void;
  onCloseCreateDialog: () => void;
  onSessionCreated: () => void;
  onCreateSession: () => void;
}

/**
 * Main content section component with page header, filters, table, and dialog
 * Contains audit sessions page header, filters, table, and create session dialog
 */
export function SessionsPageContent({
  community,
  filteredAndSortedSessions,
  filters,
  sort,
  filterOptions,
  sessionsLoading,
  filterOptionsLoading,
  isCreateDialogOpen,
  communityId,
  currentPage,
  totalPages,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
  onSortChange,
  onViewSession,
  onDeleteSuccess,
  onCloseCreateDialog,
  onSessionCreated,
  onCreateSession,
}: SessionsPageContentProps) {
  // Fetch custom values discovery at page level so the dialog opens instantly.
  // Fires once on mount; data flows as props to the dialog, matching the
  // pattern used by the other filter options (useAuditSessionFilterOptions).
  const { customValues: discoveredCustomValues, loading: discoveryLoading, errors: discoveryErrors, getCustomValues } = useCustomValuesDiscovery();
  const [showDiscoveryErrorToast, setShowDiscoveryErrorToast] = useState(false);

  useEffect(() => {
    if (communityId) {
      getCustomValues(communityId);
    }
  }, [communityId, getCustomValues]);

  useEffect(() => {
    if (discoveryErrors?.length) {
      setShowDiscoveryErrorToast(true);
    }
  }, [discoveryErrors]);

  return (
    <div className={styles.contentSection}>
      {/* Discovery Error Toast — shown at page level since the fetch fires on mount */}
      {showDiscoveryErrorToast && discoveryErrors?.length && (
        <MessageToast
          key="discovery-error"
          message="Custom values discovery failed"
          subMessage={formatErrorMessages(discoveryErrors).join('\n')}
          variant="warning"
          onClose={() => setShowDiscoveryErrorToast(false)}
        />
      )}

      <div className={styles.contentContainer}>
        {/* Page Header - moved from hero section to content section */}
        <AuditSessionsPageHeader
          community={community}
          isLoading={sessionsLoading || filterOptionsLoading}
          sessionCount={filteredAndSortedSessions.length}
          onCreateSession={onCreateSession}
        />

        {/* New Redesigned Filters Component */}
        <AuditSessionsFilters
          filterOptions={filterOptions}
          filters={filters}
          isLoading={sessionsLoading}
          pageSize={pageSize}
          sort={sort}
          onFiltersChange={onFiltersChange}
          onPageSizeChange={onPageSizeChange}
          onSortChange={onSortChange}
        />

        <AuditSessionsTable isLoading={sessionsLoading} sessions={filteredAndSortedSessions} onDeleteSession={onDeleteSuccess} onViewSession={onViewSession} />

        {/* Pagination Controls */}
        <Pagination currentPage={currentPage} disabled={sessionsLoading} totalPages={totalPages} onPageChange={onPageChange} />

        {/* Create Session Dialog */}
        <CreateAuditSessionDialog
          communityId={communityId}
          communityName={community?.name || 'Unknown Community'}
          discoveredCustomValues={discoveredCustomValues.customValuesKeys}
          discoveryLoading={discoveryLoading}
          isOpen={isCreateDialogOpen}
          onClose={onCloseCreateDialog}
          onSessionCreated={onSessionCreated}
        />
      </div>
    </div>
  );
}
